import { ChangeEvent, useCallback, useReducer, useRef } from 'react'
import { omit } from 'lodash'
import { blurActiveAndGetFinalValues, scrollToFirstError } from './helpers'

export type ValidationFn<T> = (
  values: IFormValues<T>,
  isSubmitting?: boolean
) => IFormErrors<T>
export interface IFormChangeEvent {
  name: string
  value: any
}

type FormClearValuesAction<T> = {
  type: 'CLEAR_VALUES'
  payload: (keyof IFormValues<T>)[]
}

type FormChangeValueAction = {
  type: 'CHANGE_VALUE'
  payload: IFormChangeEvent
}

type FormChangeValuesAction = {
  type: 'CHANGE_VALUES'
  payload: IFormChangeEvent[]
}

type FormBlurValueAction = {
  type: 'BLUR_VALUE'
  payload: IFormChangeEvent
}

type FormBlurValuesAction = {
  type: 'BLUR_VALUES'
  payload: IFormChangeEvent[]
}

type FormSubmitAction = {
  type: 'PRE_SUBMIT'
}

type FormResetErrorsAction = {
  type: 'RESET_ERRORS'
}

type FormChangeAction<T> =
  | FormChangeValueAction
  | FormBlurValueAction
  | FormChangeValuesAction
  | FormBlurValuesAction
  | FormSubmitAction
  | FormResetErrorsAction
  | FormClearValuesAction<T>

export type IForm<T> = {
  values: IFormValues<T>
  errors: IFormErrors<T>
}

export type IFormValues<T> = Partial<T>

export type IFormErrors<T> = Partial<{ [key in keyof T]: string | undefined }>

export interface IUseFormReturnType<
  T,
  CallbackFnArgs extends any[],
  CallbackFnReturn = void,
> {
  handleChange: (e: IFormChangeEvent) => void
  handleSubmit: (
    e: ChangeEvent<HTMLButtonElement>,
    ...args: CallbackFnArgs
  ) => void
  completeStaggeredSubmit: () => Promise<CallbackFnReturn>
  prepareForStaggeredSubmit: () => boolean
  handleBlur: (e: IFormChangeEvent) => void
  batchHandleChange: (events: IFormChangeEvent[]) => void
  batchHandleBlur: (events: IFormChangeEvent[]) => void
  clearValues: (arrayOfKeys: (keyof T)[]) => void
  clearErrors: () => void
  values: IFormValues<T>
  errors: IFormErrors<T>
}

type State<T> = {
  values: IFormValues<T>
  errors: IFormErrors<T>
  isSubmitting: boolean
}

function omitUndefined<T extends Record<string, unknown>>(obj: T): T {
  const newObj = { ...obj }
  Object.keys(newObj).forEach(
    key => newObj[key] === undefined && delete newObj[key]
  )
  return newObj
}

const useFormReducer = <T>(
  initialState: State<T>,
  validationFn: ValidationFn<T>
): [State<T>, (action: FormChangeAction<T>) => void] => {
  const [state, dispatch] = useReducer(
    (state: State<T>, action: FormChangeAction<T>): State<T> => {
      switch (action.type) {
        case 'CHANGE_VALUE': {
          const { name, value } = action.payload
          const newValues = { ...state.values, [name]: value }
          return {
            ...state,
            values: omitUndefined(newValues),
          }
        }
        case 'BLUR_VALUE': {
          const { name, value } = action.payload
          const newValues = { ...state.values, [name]: value }
          const newErrors = validationFn(newValues, state.isSubmitting)
          return {
            ...state,
            values: omitUndefined(newValues),
            errors: newErrors,
          }
        }
        case 'CHANGE_VALUES': {
          const newValues = action.payload.reduce(
            (acc, { name, value }) => ({ ...acc, [name]: value }),
            state.values
          )
          return {
            ...state,
            values: omitUndefined(newValues),
          }
        }
        case 'BLUR_VALUES': {
          const newValues = action.payload.reduce(
            (acc, { name, value }) => ({ ...acc, [name]: value }),
            state.values
          )
          const newErrors = validationFn(newValues, state.isSubmitting)
          return {
            ...state,
            values: newValues,
            errors: newErrors,
          }
        }
        case 'PRE_SUBMIT': {
          const newErrors = validationFn(state.values, true)
          return {
            ...state,
            isSubmitting: true,
            errors: newErrors,
          }
        }
        case 'RESET_ERRORS': {
          return {
            ...state,
            errors: {},
          }
        }
        case 'CLEAR_VALUES': {
          const newValues = omit<State<T>['values']>(
            state.values,
            action.payload
          )
          return {
            ...state,
            values: newValues,
          }
        }
      }
    },
    initialState
  )

  return [state, dispatch]
}

const trimValueIfString = (e: IFormChangeEvent): IFormChangeEvent =>
  typeof e.value === 'string' ? { ...e, value: e.value.trim() } : e

/**
 * Returns object of form values, corresponding object of form errors
 * and functions to handle onChange, onBlur and onSubmit form actions.
 * @param validate - a function called with the form values object
 * that returns a corresponding object of errors
 * @param callback - a function that will be called if the form validates
 * with no errors
 * @param initialValues - an optional object of with initial values to
 * instantiate the form with
 * @param CallbackFnArgs - a tuple of types corresponding to each argument
 * that will be passed into the callback function
 */
const useForm = <
  Values extends { [key: string]: any },
  CallbackFnArgs extends any[] = [],
  CallbackFnReturn = void,
>(
  validate: ValidationFn<Values>,
  callback: (
    values: Values,
    ...args: CallbackFnArgs
  ) => CallbackFnReturn | Promise<CallbackFnReturn>,
  initialValues: IFormValues<Values> = {},
  validateOnInit?: boolean,
  scrollToErrorMapping?: { [key in keyof IFormErrors<Values>]: string }
): IUseFormReturnType<Values, CallbackFnArgs, CallbackFnReturn> => {
  type ReturnFn = IUseFormReturnType<Values, CallbackFnArgs, CallbackFnReturn>
  type ValuesAfterValidation = {
    validatedValues: Values
    canSubmit: boolean
  }

  const initialState = {
    values: initialValues,
    errors: validateOnInit ? validate(initialValues) : {},
    isSubmitting: false,
  }
  const [{ values, errors }, dispatch] = useFormReducer(initialState, validate)

  const getFinalValuesAndApplyValidation = (): ValuesAfterValidation => {
    dispatch({ type: 'PRE_SUBMIT' })
    const valuesOnSubmit = blurActiveAndGetFinalValues<Values>(values)
    const submitErrors = validate(valuesOnSubmit, true)
    const isErrors = Object.values(submitErrors).some(error => !!error)
    if (isErrors) {
      scrollToErrorMapping &&
        scrollToFirstError<IFormErrors<Values>>(
          submitErrors,
          scrollToErrorMapping
        )
    }
    return { validatedValues: valuesOnSubmit, canSubmit: !isErrors }
  }

  /**
   * Accepts a callback which is called if there are no errors.
   * Sets errors and isSubmitting bool - we want errors to persist
   * in the case that validation prevents form submission.
   * @param args - An unknown number of arguments can be captured using
   * the rest parameter as a tuple/array these will later be provided to the callback
   */
  const handleSubmit: ReturnFn['handleSubmit'] = (
    e: ChangeEvent<HTMLButtonElement>,
    ...args: CallbackFnArgs
  ): void => {
    e.preventDefault()
    const { canSubmit, validatedValues } = getFinalValuesAndApplyValidation()
    if (canSubmit) {
      callback(validatedValues, ...args)
    }
  }

  const valuesAfterValidationRef = useRef<ValuesAfterValidation | null>(null)

  // N.B. These functions only work correctly if you can guarantee
  // no changes to the form between the calls to prepareForStaggeredSubmit and
  // completeStaggeredSubmit. Use with caution.
  const prepareForStaggeredSubmit = (): boolean => {
    const { canSubmit, validatedValues } = getFinalValuesAndApplyValidation()
    valuesAfterValidationRef.current = { canSubmit, validatedValues }
    return canSubmit
  }

  const completeStaggeredSubmit = async (
    ...args: CallbackFnArgs
  ): Promise<CallbackFnReturn> => {
    if (valuesAfterValidationRef.current === null) {
      throw new Error(
        'completeStaggeredSubmit called before prepareForStaggeredSubmit'
      )
    } else if (!valuesAfterValidationRef.current.canSubmit) {
      throw new Error(
        'Tried to completeStaggeredSubmit() but prepareForStaggeredSubmit() was false'
      )
    }
    const { validatedValues } = valuesAfterValidationRef.current
    return await callback(validatedValues, ...args)
  }

  /**
   * Accepts 'onChange' events and store form values in form state
   */
  const handleChange: ReturnFn['handleChange'] = useCallback(
    (e: IFormChangeEvent) => {
      dispatch({
        type: 'CHANGE_VALUE',
        payload: trimValueIfString(e),
      })
    },
    [dispatch]
  )

  const batchHandleChange: ReturnFn['batchHandleChange'] = useCallback(
    (events: IFormChangeEvent[]) => {
      const payload = events.map(e => {
        return trimValueIfString(e)
      })

      dispatch({
        type: 'CHANGE_VALUES',
        payload,
      })
    },
    [dispatch]
  )

  /**
   * Accepts 'onBlur' events, validates the form and return errors for invalid input
   */
  const handleBlur: ReturnFn['handleBlur'] = useCallback(
    (e: IFormChangeEvent) => {
      dispatch({
        type: 'BLUR_VALUE',
        payload: trimValueIfString(e),
      })
    },
    [dispatch]
  )

  const batchHandleBlur: ReturnFn['batchHandleBlur'] = useCallback(
    (events: IFormChangeEvent[]) => {
      dispatch({
        type: 'BLUR_VALUES',
        payload: events.map(trimValueIfString),
      })
    },
    [dispatch]
  )

  /**
   * Accepts an array of keys, removes specified keys from the values object
   */
  const clearValues: ReturnFn['clearValues'] = useCallback(
    (arrayOfKeys: (keyof Values)[]) => {
      dispatch({
        type: 'CLEAR_VALUES',
        payload: arrayOfKeys,
      })
    },
    [dispatch]
  )

  /**
   * Empties errors generated from executing validate()
   */
  const clearErrors: ReturnFn['clearErrors'] = useCallback(
    () => dispatch({ type: 'RESET_ERRORS' }),
    [dispatch]
  )

  return {
    handleChange,
    handleSubmit,
    prepareForStaggeredSubmit,
    completeStaggeredSubmit,
    handleBlur,
    batchHandleChange,
    batchHandleBlur,
    clearValues,
    clearErrors,
    values,
    errors,
  }
}

export default useForm
